import { useEffect, useState, createContext, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { AiResultDialog, useAiRunner } from "@/components/ai/AiResultDialog";
import { Section } from "@/components/settings/Section";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { GitCompareArrows, RefreshCw, ChevronDown, ChevronRight, ShieldCheck, AlertTriangle, ServerCog, Cloud, ToggleLeft, UserX, Trash2, GitMerge, Mail, EyeOff, Undo2, Download, Zap, Tag, Sparkles } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const ageMin = (iso) => (iso ? Math.round((Date.now() - new Date(iso).getTime()) / 60000) : null);
const TONE = { amber: "text-amber-700 bg-amber-50 border-amber-200", rose: "text-rose-700 bg-rose-50 border-rose-200", blue: "text-blue-700 bg-blue-50 border-blue-200" };

const DriftAiCtx = createContext(null);

function DriftRow({ label, items = [], tone = "amber", testid, kind, keyOf, renderItem, actions = [], busy, onBulk, onIgnore, ignoreLabel }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const driftAi = useContext(DriftAiCtx);
  const count = items.length;
  if (!count) return null;
  const keyed = keyOf ? items.map(keyOf).filter(Boolean) : [];
  const toggle = (k) => setSelected((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const allChecked = keyed.length > 0 && selected.size === keyed.length;
  const targets = selected.size ? [...selected] : keyed;
  const hasToolbar = keyed.length > 0 && (actions.length > 0 || kind);

  return (
    <div className="border border-zinc-200">
      <div className="flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-zinc-50">
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`reconcile-drift-${testid}`}
          className="flex flex-1 items-center gap-2 text-left text-zinc-700">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {label}
        </button>
        <div className="flex items-center gap-2">
          {driftAi?.aiOn && (
            <button type="button" onClick={() => driftAi.onExplain(label, items)} data-testid={`ai-explain-drift-${testid}`} title={driftAi.explainLabel}
              className="inline-flex items-center gap-1 rounded-sm border border-primary/30 bg-primary/5 px-1.5 py-0.5 text-[10px] font-semibold text-primary hover:bg-primary/10">
              <Sparkles size={11} /> {driftAi.explainLabel}
            </button>
          )}
          <span className={`rounded-sm border px-2 py-0.5 font-mono text-xs ${TONE[tone]}`}>{count}</span>
        </div>
      </div>
      {open && (
        <div className="border-t border-zinc-100 bg-zinc-50">
          {hasToolbar && (
            <div className="flex flex-wrap items-center gap-2 border-b border-zinc-100 px-3 py-2">
              <label className="flex items-center gap-1.5 text-[11px] text-zinc-500">
                <input type="checkbox" checked={allChecked} onChange={(e) => setSelected(e.target.checked ? new Set(keyed) : new Set())}
                  data-testid={`reconcile-selectall-${testid}`} className="h-3.5 w-3.5 accent-primary" />
                {selected.size ? `${selected.size} selected` : "Select all"}
              </label>
              <span className="text-zinc-300">·</span>
              {actions.map((a) => (
                <button key={a.action} onClick={() => onBulk(a.action, targets, a)} disabled={!!busy || targets.length === 0}
                  data-testid={`reconcile-bulk-${a.action}-${testid}`}
                  className={`inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[11px] font-semibold disabled:opacity-50 ${a.tone === "danger" ? "border-red-200 text-red-600 hover:bg-red-50" : a.tone === "warn" ? "border-amber-300 text-amber-700 hover:bg-amber-50" : "border-zinc-300 text-primary hover:bg-zinc-100"}`}>
                  <a.icon size={12} /> {a.label} ({targets.length})
                </button>
              ))}
              {kind && (
                <button onClick={() => onIgnore(targets, kind)} disabled={!!busy || targets.length === 0}
                  data-testid={`reconcile-ignore-${testid}`}
                  className="inline-flex items-center gap-1 rounded-sm border border-zinc-300 px-2 py-0.5 text-[11px] font-semibold text-zinc-500 hover:bg-zinc-100 disabled:opacity-50">
                  <EyeOff size={12} /> {ignoreLabel} ({targets.length})
                </button>
              )}
            </div>
          )}
          <div className="divide-y divide-zinc-100">
            {items.map((it, i) => {
              const k = keyOf ? keyOf(it) : null;
              return (
                <div key={i} className="flex items-center gap-2 px-3 py-1.5 font-mono text-[11px] text-zinc-600" data-testid={`reconcile-item-${testid}-${i}`}>
                  {hasToolbar && k && (<input type="checkbox" checked={selected.has(k)} onChange={() => toggle(k)} data-testid={`reconcile-check-${testid}-${i}`} className="h-3.5 w-3.5 shrink-0 accent-primary" />)}
                  <span className="min-w-0 flex-1">{renderItem(it)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function ReconcileSection({ s, setS }) {
  const { t, lang } = useLang();
  const { ai } = useAuth();
  const aiRunner = useAiRunner();
  const aiOn = !!(ai?.available && ai?.canUse);
  const [report, setReport] = useState(null);
  const [inv, setInv] = useState({ users: null, groups: null });
  const [history, setHistory] = useState([]);
  const [ignores, setIgnores] = useState([]);
  const [showIgnores, setShowIgnores] = useState(false);
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [viewingRun, setViewingRun] = useState(null);
  const [testingDigest, setTestingDigest] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const cfg = s.onPremReconcile || {};
  const digest = cfg.digest || {};
  const autoRem = cfg.autoRemediate || {};
  const autoActions = autoRem.actions || {};
  const setCfg = (k, v) => setS({ ...s, onPremReconcile: { ...s.onPremReconcile, [k]: v } });
  const setDigest = (k, v) => setS({ ...s, onPremReconcile: { ...s.onPremReconcile, digest: { ...digest, [k]: v } } });
  const setAutoRem = (k, v) => setS({ ...s, onPremReconcile: { ...s.onPremReconcile, autoRemediate: { ...autoRem, [k]: v } } });
  const setAutoAction = (k, v) => setS({ ...s, onPremReconcile: { ...s.onPremReconcile, autoRemediate: { ...autoRem, actions: { ...autoActions, [k]: v } } } });

  const loadIgnores = async () => { try { const { data } = await api.get("/reconcile/ignores"); setIgnores(data || []); } catch (e) { /* */ } };
  const load = async () => {
    try { const { data } = await api.get("/reconcile/report"); setReport(data.report); setInv(data.inventory || { users: null, groups: null }); } catch (e) { /* */ }
    try { const { data } = await api.get("/reconcile/reports?limit=12"); setHistory(data || []); } catch (e) { /* */ }
    loadIgnores();
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Deep-link: /settings?tab=sync&report=<id> loads that specific historical run's breakdown.
  useEffect(() => {
    const rid = searchParams.get("report");
    if (!rid) { setViewingRun(null); return; }
    api.get(`/reconcile/report/${rid}`)
      .then(({ data }) => { setReport(data.report); setViewingRun(data.report); })
      .catch(() => { toast.error(t("reconcile.runNotFound")); });
    // eslint-disable-next-line
  }, [searchParams]);

  const backToLatest = () => {
    const p = new URLSearchParams(searchParams); p.delete("report"); setSearchParams(p);
    setViewingRun(null); load();
  };

  const sendTestDigest = async () => {
    setTestingDigest(true);
    try {
      const { data } = await api.post("/reconcile/digest/test");
      if (data.sent) {
        const ch = [data.channels?.teams && "Teams", data.channels?.email && "Email"].filter(Boolean).join(" + ");
        toast.success(t("reconcile.digestTestSent").replace("{ch}", ch));
      } else {
        toast.error(t("reconcile.digestTestFail") + (data.errors?.length ? ` — ${data.errors.join("; ")}` : ""));
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setTestingDigest(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/reconcile/run"); setReport(data); await load();
      if (data.status === "noInventory") toast.warning(t("reconcile.noInventory"));
      else if (data.status === "clean") toast.success(t("reconcile.clean"));
      else toast.success(t("reconcile.doneWithDrift").replace("{n}", data.summary?.totalMismatches ?? 0));
    } catch (e) { toast.error(apiErr(e)); } finally { setRunning(false); }
  };

  const doBulk = async (action, keys, confirm) => {
    setBusy(true);
    const tId = toast.loading(t("reconcile.bulkProgress").replace("{i}", "0").replace("{total}", keys.length));
    let ok = 0;
    for (let i = 0; i < keys.length; i++) {
      try { await api.post("/reconcile/remediate", { action, key: keys[i], confirm, rerun: false }); ok++; } catch (e) { /* keep going */ }
      toast.loading(t("reconcile.bulkProgress").replace("{i}", i + 1).replace("{total}", keys.length), { id: tId });
    }
    try { const { data } = await api.post("/reconcile/run"); setReport(data); } catch (e) { /* */ }
    await load();
    toast.success(t("reconcile.bulkDone").replace("{ok}", ok).replace("{total}", keys.length), { id: tId });
    setBusy(false); setConfirmState(null); setConfirmText("");
  };

  const onBulk = (action, keys, meta = {}) => {
    if (!keys.length) return;
    if (meta.destructive) { setConfirmState({ action, keys, label: `${keys.length}`, mode: "delete" }); setConfirmText(""); }
    else if (meta.confirm === "rename") {
      const items = report?.users?.samMismatch || [];
      const preview = keys.map((k) => {
        const it = items.find((u) => (u.entraSamAccountName || (u.userPrincipalName || "").split("@")[0]) === k);
        return it
          ? { key: k, upn: it.userPrincipalName, adSam: it.samAccountName, newSam: (it.userPrincipalName || "").split("@")[0] }
          : { key: k, newSam: k };
      });
      setConfirmState({ action, keys, label: `${keys.length}`, mode: "rename", preview });
      setConfirmText("");
    } else doBulk(action, keys);
  };

  const doIgnore = async (keys, kind) => {
    if (!keys.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/reconcile/ignore", { kind, keys });
      toast.success(t("reconcile.ignoredToast").replace("{n}", data.count));
      if (data.report) setReport(data.report);
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const unignore = async (kind, key) => {
    setBusy(true);
    try { await api.delete(`/reconcile/ignore/${kind}/${encodeURIComponent(key)}`); await load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/reconcile/reports.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "reconcile-history.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const sm = report?.summary || {};
  const statusBadge = () => {
    if (!report) return null;
    const map = {
      clean: ["text-emerald-700 bg-emerald-50 border-emerald-200", ShieldCheck, t("reconcile.stClean")],
      drift: ["text-amber-700 bg-amber-50 border-amber-200", AlertTriangle, t("reconcile.stDrift")],
      stale: ["text-amber-700 bg-amber-50 border-amber-200", AlertTriangle, t("reconcile.stStale")],
      noInventory: ["text-zinc-600 bg-zinc-100 border-zinc-200", ServerCog, t("reconcile.stNoInv")],
    };
    const [cls, Icon, lbl] = map[report.status] || map.noInventory;
    return <span className={`inline-flex items-center gap-1.5 rounded-sm border px-2.5 py-1 text-xs font-bold ${cls}`}><Icon size={13} /> {lbl}</span>;
  };

  const invAge = ageMin(inv.users?.capturedAt || inv.groups?.capturedAt);
  const invStale = cfg.inventoryMaxAgeMinutes > 0 && invAge != null && invAge > cfg.inventoryMaxAgeMinutes;

  const explainCategory = (label, items) => aiRunner.run(`${t("ai.driftTitle")} — ${label}`, "/ai/explain-drift", { language: lang, category: label, items });

  return (
    <DriftAiCtx.Provider value={{ aiOn, onExplain: explainCategory, explainLabel: t("ai.explain") }}>
    <Section icon={GitCompareArrows} title={t("reconcile.title")}>
      <label className="flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-zinc-900">{t("reconcile.enableHeading")}</div>
          <div className="text-sm text-zinc-500">{t("reconcile.enableDesc")}</div>
        </div>
        <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg("enabled", v)} data-testid="settings-reconcile-enabled" />
      </label>

      <div className="mt-4 grid grid-cols-1 gap-5 border-t border-zinc-100 pt-4 md:grid-cols-3">
        <label className="flex items-center gap-2.5 text-sm text-zinc-700">
          <Switch checked={cfg.includeMembership !== false} onCheckedChange={(v) => setCfg("includeMembership", v)} data-testid="settings-reconcile-membership" />
          {t("reconcile.includeMembership")}
        </label>
        <label className="flex items-center gap-2.5 text-sm text-zinc-700">
          <Switch checked={!!cfg.notify} onCheckedChange={(v) => setCfg("notify", v)} data-testid="settings-reconcile-notify" />
          {t("reconcile.notify")}
        </label>
        <div className="space-y-1.5">
          <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("reconcile.maxAge")}</Label>
          <Input type="number" min={0} max={43200} value={cfg.inventoryMaxAgeMinutes ?? 180}
                 onChange={(e) => setCfg("inventoryMaxAgeMinutes", Number(e.target.value))}
                 data-testid="settings-reconcile-maxage" className="w-32 rounded-sm border-zinc-300" />
        </div>
      </div>

      <div className="mt-4 border border-zinc-200 bg-zinc-50 p-3">
        <label className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-bold text-zinc-800"><Mail size={15} className="text-zinc-500" /> {t("reconcile.digestHeading")}</div>
          <Switch checked={!!digest.enabled} onCheckedChange={(v) => setDigest("enabled", v)} data-testid="settings-reconcile-digest-enabled" />
        </label>
        <div className="mt-1 text-xs text-zinc-500">{t("reconcile.digestDesc")}</div>
        {digest.enabled && (
          <div className="mt-3 space-y-3">
            <div className="flex items-center gap-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("reconcile.digestFreq")}</Label>
              <Select value={digest.frequency || "weekly"} onValueChange={(v) => setDigest("frequency", v)}>
                <SelectTrigger className="h-8 w-36 rounded-sm border-zinc-300" data-testid="settings-reconcile-digest-freq"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">{t("reconcile.daily")}</SelectItem>
                  <SelectItem value="weekly">{t("reconcile.weekly")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">{t("reconcile.digestEmails")}</Label>
                <Input value={digest.emails || ""} onChange={(e) => setDigest("emails", e.target.value)}
                       placeholder="it@corp.com, secops@corp.com" data-testid="settings-reconcile-digest-emails" className="rounded-sm border-zinc-300" />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">{t("reconcile.digestTeams")}</Label>
                <Input value={digest.teamsWebhook || ""} onChange={(e) => setDigest("teamsWebhook", e.target.value)}
                       placeholder="https://…/workflows/…" data-testid="settings-reconcile-digest-teams" className="rounded-sm border-zinc-300 font-mono text-xs" />
              </div>
            </div>
            <p className="text-[11px] text-zinc-400">{t("reconcile.digestRecipientsHint")}</p>
            <Button variant="outline" size="sm" onClick={sendTestDigest} disabled={testingDigest}
                    data-testid="reconcile-digest-test" className="rounded-sm border-zinc-300">
              <Mail size={13} className={`mr-1.5 ${testingDigest ? "animate-pulse" : ""}`} /> {t("reconcile.digestTest")}
            </Button>
          </div>
        )}
      </div>

      <div className="mt-4 border border-zinc-200 bg-zinc-50 p-3" data-testid="reconcile-autoremediate">
        <label className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-bold text-zinc-800"><Zap size={15} className="text-amber-500" /> {t("reconcile.autoHeading")}</div>
          <Switch checked={!!autoRem.enabled} onCheckedChange={(v) => setAutoRem("enabled", v)} data-testid="settings-reconcile-auto-enabled" />
        </label>
        <div className="mt-1 text-xs text-zinc-500">{t("reconcile.autoDesc")}</div>
        {autoRem.enabled && (
          <div className="mt-3 space-y-2.5">
            <label className="flex items-start gap-2.5 text-sm text-zinc-700">
              <Switch checked={!!autoActions.matchAd} onCheckedChange={(v) => setAutoAction("matchAd", v)} data-testid="settings-reconcile-auto-matchAd" />
              <span><span className="font-semibold">{t("reconcile.fixMatchAd")}</span> — <span className="text-zinc-500">{t("reconcile.autoMatchAdDesc")}</span></span>
            </label>
            <label className="flex items-start gap-2.5 text-sm text-zinc-700">
              <Switch checked={!!autoActions.reconcileMembership} onCheckedChange={(v) => setAutoAction("reconcileMembership", v)} data-testid="settings-reconcile-auto-membership" />
              <span><span className="font-semibold">{t("reconcile.fixReconcileMembership")}</span> — <span className="text-zinc-500">{t("reconcile.autoMembershipDesc")}</span></span>
            </label>
            <label className="flex items-start gap-2.5 text-sm text-zinc-700">
              <Switch checked={!!autoActions.forceSync} onCheckedChange={(v) => setAutoAction("forceSync", v)} data-testid="settings-reconcile-auto-forceSync" />
              <span><span className="font-semibold">{t("reconcile.fixSync")}</span> — <span className="text-zinc-500">{t("reconcile.autoForceSyncDesc")}</span></span>
            </label>
            <p className="flex items-start gap-1.5 text-[11px] text-amber-600"><AlertTriangle size={13} className="mt-0.5 shrink-0" /> {t("reconcile.autoWarn")}</p>
            {autoRem.lastRun?.at && (
              <div className="rounded-sm border border-zinc-200 bg-white px-2.5 py-1.5 text-[11px] text-zinc-600" data-testid="reconcile-auto-lastrun">
                <span className="font-semibold text-zinc-700">{t("reconcile.autoLastRun")}:</span> {fmt(autoRem.lastRun.at)} — {t("reconcile.autoFixedN").replace("{n}", autoRem.lastRun.fixed ?? 0)}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="reconcile-inventory-status">
        <div className="border border-zinc-200 p-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">{t("reconcile.adUsers")}</div>
          <div className="mt-1 text-lg font-bold text-zinc-900">{inv.users?.count ?? "—"}</div>
          <div className="font-mono text-[11px] text-zinc-400">{fmt(inv.users?.capturedAt)}{inv.users?.agentId ? ` · ${inv.users.agentId}` : ""}</div>
        </div>
        <div className="border border-zinc-200 p-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">{t("reconcile.adGroups")}</div>
          <div className="mt-1 text-lg font-bold text-zinc-900">{inv.groups?.count ?? "—"}</div>
          <div className="font-mono text-[11px] text-zinc-400">{fmt(inv.groups?.capturedAt)}{inv.groups?.agentId ? ` · ${inv.groups.agentId}` : ""}</div>
        </div>
      </div>
      {invStale && <p className="mt-2 text-xs font-medium text-amber-600" data-testid="reconcile-stale-warning">{t("reconcile.staleWarn").replace("{n}", invAge)}</p>}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-zinc-100 pt-4">
        <div className="flex items-center gap-3">
          {statusBadge()}
          {report && <span className="font-mono text-[11px] text-zinc-400">{t("reconcile.lastRun")}: {fmt(report.ranAt)}</span>}
        </div>
        <div className="flex items-center gap-2">
          {aiOn && report && report.status !== "noInventory" && (
            <Button variant="outline" onClick={() => aiRunner.run(t("ai.driftTitle"), "/ai/explain-drift", { language: lang })}
              data-testid="ai-explain-drift" className="rounded-sm border-primary/30 bg-primary/5 text-primary hover:bg-primary/10">
              <Sparkles size={15} className="mr-2" /> {t("ai.explainDrift")}
            </Button>
          )}
          <Button variant="outline" onClick={runNow} disabled={running} data-testid="reconcile-run-now" className="rounded-sm border-zinc-300">
            <RefreshCw size={15} className={`mr-2 ${running ? "animate-spin" : ""}`} /> {t("reconcile.runNow")}
          </Button>
        </div>
      </div>
      <AiResultDialog open={aiRunner.state.open} onOpenChange={(v) => !v && aiRunner.close()} title={aiRunner.state.title} loading={aiRunner.state.loading} error={aiRunner.state.error} content={aiRunner.state.content} sources={aiRunner.state.sources} empty={aiRunner.state.empty} />

      {history.length > 0 && (
        <div className="mt-4" data-testid="reconcile-history">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">{t("reconcile.history")}</div>
            <button onClick={exportCsv} data-testid="reconcile-export-csv" className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"><Download size={12} /> {t("reconcile.exportCsv")}</button>
          </div>
          <div className="flex items-end gap-1.5 overflow-x-auto pb-1">
            {[...history].reverse().map((h) => {
              const n = (h.summary || {}).totalMismatches ?? 0;
              const barCls = h.status === "clean" ? "bg-emerald-400" : h.status === "noInventory" ? "bg-zinc-300" : "bg-amber-400";
              const active = viewingRun?.id === h.id;
              return (
                <button key={h.id} onClick={() => { const p = new URLSearchParams(searchParams); p.set("report", h.id); setSearchParams(p); }}
                  title={`${fmt(h.ranAt)} · ${n} ${t("reconcile.mismatches")}`} className="flex flex-col items-center gap-1" data-testid="reconcile-history-bar">
                  <span className="font-mono text-[9px] text-zinc-400">{n}</span>
                  <div className={`w-4 rounded-sm ${barCls} ${active ? "ring-2 ring-primary ring-offset-1" : "hover:opacity-70"}`} style={{ height: `${Math.min(56, 6 + n * 6)}px` }} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {viewingRun && (
        <div className="mt-3 flex items-center justify-between rounded-sm border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800" data-testid="reconcile-viewing-historical">
          <span>{t("reconcile.viewingRun").replace("{d}", fmt(viewingRun.ranAt))}</span>
          <button onClick={backToLatest} data-testid="reconcile-back-latest" className="font-semibold underline hover:no-underline">{t("reconcile.backToLatest")}</button>
        </div>
      )}

      {report && report.status !== "noInventory" && (
        <div className="mt-3 space-y-2" data-testid="reconcile-health-panel">
          <DriftRow testid="users-missing-entra" label={t("reconcile.usersMissingEntra")} tone="rose" busy={busy} onBulk={onBulk} onIgnore={doIgnore} ignoreLabel={t("reconcile.ignore")} kind="user"
            items={report.users?.missingInEntra || []} keyOf={(u) => u.samAccountName}
            actions={[{ action: "forceSync", label: t("reconcile.fixSync"), icon: Cloud }]}
            renderItem={(u) => <span className="block truncate">{u.samAccountName || u.userPrincipalName} · {u.displayName}</span>} />
          <DriftRow testid="users-missing-ad" label={t("reconcile.usersMissingAd")} tone="amber" busy={busy} onBulk={onBulk} onIgnore={doIgnore} ignoreLabel={t("reconcile.ignore")} kind="user"
            items={report.users?.missingInAd || []} keyOf={(u) => u.samAccountName || (u.userPrincipalName || "").split("@")[0]}
            actions={[{ action: "disableEntra", label: t("reconcile.fixDisable"), icon: ToggleLeft },
                      { action: "deleteEntra", label: t("reconcile.fixDelete"), icon: Trash2, tone: "danger", destructive: true }]}
            renderItem={(u) => <span className="block truncate">{u.userPrincipalName} · {u.displayName}</span>} />
          <DriftRow testid="users-enabled-mismatch" label={t("reconcile.enabledMismatch")} tone="amber" busy={busy} onBulk={onBulk} onIgnore={doIgnore} ignoreLabel={t("reconcile.ignore")} kind="user"
            items={report.users?.enabledMismatch || []} keyOf={(u) => u.samAccountName}
            actions={[{ action: "matchAd", label: t("reconcile.fixMatchAd"), icon: UserX }]}
            renderItem={(u) => <span className="block truncate">{u.userPrincipalName} · AD={String(u.adEnabled)} / Entra={String(u.entraEnabled)}</span>} />
          <DriftRow testid="users-sam-mismatch" label={t("reconcile.samMismatch")} tone="amber" busy={busy} onBulk={onBulk} onIgnore={doIgnore} ignoreLabel={t("reconcile.ignore")} kind="user"
            items={report.users?.samMismatch || []} keyOf={(u) => u.entraSamAccountName || (u.userPrincipalName || "").split("@")[0]}
            actions={[{ action: "resetSamToUpn", label: t("reconcile.fixResetSam"), icon: Tag, tone: "warn", confirm: "rename" }]}
            renderItem={(u) => <span className="block truncate"><Tag size={11} className="mr-1 inline text-amber-500" />{u.userPrincipalName} · AD={u.samAccountName} / Entra={u.entraSamAccountName} <span className="font-semibold text-primary">→ {(u.userPrincipalName || "").split("@")[0]}</span> {u.matchedBy ? `· ${t("reconcile.matchedBy")} ${u.matchedBy}` : ""}</span>} />
          <DriftRow testid="groups-missing-entra" label={t("reconcile.groupsMissingEntra")} tone="rose" busy={busy} onIgnore={doIgnore} ignoreLabel={t("reconcile.ignore")} kind="group"
            items={report.groups?.missingInEntra || []} keyOf={(g) => g.samAccountName || g.name}
            renderItem={(g) => <span className="block truncate">{g.samAccountName || g.name}</span>} />
          <DriftRow testid="groups-missing-ad" label={t("reconcile.groupsMissingAd")} tone="amber" busy={busy} onBulk={onBulk} onIgnore={doIgnore} ignoreLabel={t("reconcile.ignore")} kind="group"
            items={report.groups?.missingInAd || []} keyOf={(g) => g.samAccountName || g.displayName}
            actions={[{ action: "deleteEntraGroup", label: t("reconcile.fixDelete"), icon: Trash2, tone: "danger", destructive: true }]}
            renderItem={(g) => <span className="block truncate">{g.displayName}</span>} />
          <DriftRow testid="membership-divergence" label={t("reconcile.membershipDivergence")} tone="blue" busy={busy} onBulk={onBulk} onIgnore={doIgnore} ignoreLabel={t("reconcile.ignore")} kind="group"
            items={report.groups?.membershipDivergence || []} keyOf={(m) => m.samAccountName}
            actions={[{ action: "reconcileMembership", label: t("reconcile.fixReconcileMembership"), icon: GitMerge }]}
            renderItem={(m) => <span className="block truncate"><b>{m.group}</b> — {t("reconcile.onlyAd")}: {m.onlyInAdCount} · {t("reconcile.onlyEntra")}: {m.onlyInEntraCount}</span>} />
          <DriftRow testid="reconcile-errors" label={t("reconcile.errors")} tone="rose" items={report.errors || []}
            renderItem={(e) => <span className="block truncate">{e}</span>} />
          {sm.totalMismatches === 0 && !sm.errors && (<p className="text-xs text-emerald-600" data-testid="reconcile-no-drift">{t("reconcile.clean")}</p>)}
        </div>
      )}

      {/* Ignored objects manager */}
      <div className="mt-3 border border-zinc-200">
        <button type="button" onClick={() => setShowIgnores((o) => !o)} data-testid="reconcile-ignores-toggle"
          className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-zinc-600 hover:bg-zinc-50">
          <span className="flex items-center gap-2">{showIgnores ? <ChevronDown size={14} /> : <ChevronRight size={14} />} <EyeOff size={13} /> {t("reconcile.ignoredList")}</span>
          <span className="rounded-sm border border-zinc-200 bg-zinc-100 px-2 py-0.5 font-mono text-xs text-zinc-500">{ignores.length}</span>
        </button>
        {showIgnores && (
          <div className="divide-y divide-zinc-100 border-t border-zinc-100 bg-zinc-50">
            {ignores.length === 0 && <div className="px-3 py-3 text-xs text-zinc-400">{t("reconcile.noIgnores")}</div>}
            {ignores.map((ig) => (
              <div key={`${ig.kind}-${ig.key}`} className="flex items-center justify-between gap-3 px-3 py-1.5 font-mono text-[11px] text-zinc-600" data-testid={`reconcile-ignored-${ig.kind}-${ig.key}`}>
                <span className="truncate"><span className="rounded-sm border border-zinc-200 bg-white px-1 text-[10px] uppercase">{ig.kind}</span> {ig.displayKey || ig.key}{ig.note ? ` · ${ig.note}` : ""}</span>
                <button onClick={() => unignore(ig.kind, ig.key)} disabled={busy} data-testid={`reconcile-unignore-${ig.kind}-${ig.key}`} className="inline-flex items-center gap-1 text-primary hover:underline disabled:opacity-50"><Undo2 size={12} /> {t("reconcile.unignore")}</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!confirmState} onOpenChange={(o) => { if (!o) { setConfirmState(null); setConfirmText(""); } }}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="reconcile-confirm-dialog">
          {confirmState?.mode === "rename" ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-base">{t("reconcile.confirmRenameTitle")}</DialogTitle>
                <DialogDescription>{t("reconcile.confirmRenameBody").replace("{n}", confirmState?.label || "")}</DialogDescription>
              </DialogHeader>
              <p className="flex items-start gap-1.5 rounded-sm border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-700"><AlertTriangle size={13} className="mt-0.5 shrink-0" /> {t("reconcile.confirmRenameWarn")}</p>
              <div className="max-h-52 overflow-y-auto rounded-sm border border-zinc-200" data-testid="reconcile-rename-preview">
                {(confirmState?.preview || []).map((p, i) => (
                  <div key={i} className="flex items-center gap-2 border-b border-zinc-100 px-2.5 py-1.5 font-mono text-[11px] last:border-b-0" data-testid={`reconcile-rename-preview-row-${i}`}>
                    <span className="min-w-0 flex-1 truncate text-zinc-500">{p.upn || p.key}</span>
                    <span className="shrink-0 text-zinc-400">{p.adSam || "—"}</span>
                    <span className="shrink-0 text-zinc-300">→</span>
                    <span className="shrink-0 font-semibold text-primary">{p.newSam}</span>
                  </div>
                ))}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => { setConfirmState(null); setConfirmText(""); }} data-testid="reconcile-confirm-cancel" className="rounded-sm border-zinc-300">{t("jobs.cancel")}</Button>
                <Button onClick={() => doBulk(confirmState.action, confirmState.keys, "RENAME")} disabled={busy}
                        data-testid="reconcile-confirm-rename" className="rounded-sm bg-amber-600 hover:bg-amber-700"><Tag size={14} className="mr-1.5" /> {t("reconcile.fixResetSam")}</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-base">{t("reconcile.confirmDeleteTitle")}</DialogTitle>
                <DialogDescription>{t("reconcile.confirmDeleteBody")} <b>{confirmState?.label}</b> {t("reconcile.confirmObjects")}</DialogDescription>
              </DialogHeader>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" data-testid="reconcile-confirm-input" className="rounded-sm border-zinc-300 font-mono" />
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => { setConfirmState(null); setConfirmText(""); }} data-testid="reconcile-confirm-cancel" className="rounded-sm border-zinc-300">{t("jobs.cancel")}</Button>
                <Button onClick={() => doBulk(confirmState.action, confirmState.keys, confirmText)} disabled={confirmText.trim().toUpperCase() !== "DELETE" || busy}
                        data-testid="reconcile-confirm-delete" className="rounded-sm bg-red-600 hover:bg-red-700"><Trash2 size={14} className="mr-1.5" /> {t("reconcile.fixDelete")}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Section>
    </DriftAiCtx.Provider>
  );
}
